import { useCallback, useEffect, useMemo, useState } from 'react'
import type { PmmRepository } from '../data/repository'
import type { CoachCandidateEditableDraft } from '../domain/types'
import type { CoachCandidateBundle, CoachCandidateRecord, CoachProgressMemoryView, CoachReadinessPacket, RelationshipRunProjection } from '../domain/coach'
import { ONBOARDING_RESTART_CONFIRMATION, type OnboardingSession } from '../domain/onboarding'
import { PostOnboardingGuide } from './PostOnboardingGuide'
import '../coach.css'
import '../loop07-coach.css'

interface CoachWorkspaceProps {
  repository: PmmRepository
  onOnboardingRestarted?: () => void
}

const activeRunStates = new Set(['dispatching', 'pending_callback', 'retrying_validation'])
const activeProgressStates = new Set(['dispatching', 'pending_callback', 'processing_callback'])

function mutationId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`
}

function editableDraftFor(candidate: CoachCandidateRecord): CoachCandidateEditableDraft {
  const source = candidate.editableDraft
  const generated = candidate.generatedAsset
  return {
    ...source,
    plainLanguageExplanation: source.plainLanguageExplanation ?? generated.plainLanguageExplanation ?? '',
    recognitionStory: source.recognitionStory ?? generated.recognitionStory ?? '',
    realLifeExamples: source.realLifeExamples ?? generated.realLifeExamples ?? [],
    responseOptions: source.responseOptions ?? generated.responseOptions ?? [],
    exampleAnswers: source.exampleAnswers ?? generated.exampleAnswers ?? [],
    responseOptional: source.responseOptional ?? generated.responseOptional ?? true,
    freeTextOptional: source.freeTextOptional ?? generated.freeTextOptional ?? true,
    lighterAlternative: source.lighterAlternative ?? generated.lighterAlternative ?? '',
  }
}

export function CoachWorkspace({ repository, onOnboardingRestarted }: CoachWorkspaceProps) {
  const [readiness, setReadiness] = useState<CoachReadinessPacket | null>(null)
  const [progressMemory, setProgressMemory] = useState<CoachProgressMemoryView | null>(null)
  const [onboarding, setOnboarding] = useState<OnboardingSession | null>(null)
  const [run, setRun] = useState<RelationshipRunProjection | null>(null)
  const [bundle, setBundle] = useState<CoachCandidateBundle | null>(null)
  const [drafts, setDrafts] = useState<Record<string, CoachCandidateEditableDraft>>({})
  const [busy, setBusy] = useState('')
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const [restartAcknowledged, setRestartAcknowledged] = useState(false)
  const [restartPhrase, setRestartPhrase] = useState('')

  const [restartMutationId, setRestartMutationId] = useState(() => mutationId('onboarding-restart'))
  const loadCandidateSet = useCallback(async (candidateSetId: string) => {
    const next = await repository.getCoachCandidateSet(candidateSetId)
    setBundle(next)
    setDrafts(Object.fromEntries(next.candidates.map((candidate) => [candidate.candidateId, editableDraftFor(candidate)])))
  }, [repository])

  const loadRun = useCallback(async (runId: string) => {
    const next = await repository.getRelationshipRunStatus(runId)
    setRun(next)
    if (next.candidateSetId) await loadCandidateSet(next.candidateSetId)
    return next
  }, [loadCandidateSet, repository])

  const loadProgressMemory = useCallback(async () => {
    const next = await repository.getCoachProgressMemory()
    setProgressMemory(next)
    return next
  }, [repository])

  const refreshReadiness = useCallback(async () => {
    const [next, currentOnboarding] = await Promise.all([repository.getCoachRelationshipReadiness(), repository.getOnboardingSession(), loadProgressMemory()])
    setReadiness(next)
    setOnboarding(currentOnboarding)
    if (next.latestRun) await loadRun(next.latestRun.runId)
  }, [loadProgressMemory, loadRun, repository])

  useEffect(() => {
    void refreshReadiness().catch(() => setError('Coach readiness could not be loaded. Your client library is unchanged.'))
  }, [refreshReadiness])

  useEffect(() => {
    if (!progressMemory?.latestRun || !activeProgressStates.has(progressMemory.latestRun.state)) return undefined
    const timer = window.setInterval(() => { void loadProgressMemory().catch(() => undefined) }, 4000)
    return () => window.clearInterval(timer)
  }, [loadProgressMemory, progressMemory?.latestRun])

  useEffect(() => {
    if (!run || !activeRunStates.has(run.state)) return undefined
    const timer = window.setInterval(() => {
      void loadRun(run.runId).catch(() => undefined)
    }, 3500)
    return () => window.clearInterval(timer)
  }, [loadRun, run])

  const counts = useMemo(() => {
    const candidates = bundle?.candidates ?? []
    return {
      approved: candidates.filter((candidate) => candidate.reviewState === 'approved').length,
      rejected: candidates.filter((candidate) => candidate.reviewState === 'rejected').length,
      published: candidates.filter((candidate) => candidate.reviewState === 'published').length,
      awaiting: candidates.filter((candidate) => ['awaiting_review', 'editing'].includes(candidate.reviewState)).length,
    }
  }, [bundle])

  async function startRun(generationMode: 'standard' | 'full_library_refresh' = 'standard') {
    const busyKey = generationMode === 'full_library_refresh' ? 'full-refresh' : 'run'
    setBusy(busyKey)
    setError('')
    try {
      const next = await repository.startRelationshipExpansion(mutationId(generationMode === 'full_library_refresh' ? 'coach-full-refresh' : 'coach-run'), generationMode)
      setRun(next)
      setBundle(null)
      setNotice(next.state === 'candidate_review_ready' ? 'The existing canonical candidate set was reopened.' : generationMode === 'full_library_refresh' ? 'A fresh 18-candidate guided library is being generated for private Coach review. The User library is unchanged.' : 'CustomGhost generation started. The current client library remains active while it runs.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The coach run could not start. The current library remains active.')
    } finally {
      setBusy('')
    }
  }


  async function updateProgressMemory() {
    setBusy('progress-memory')
    setError('')
    setNotice('')
    try {
      const next = await repository.startCoachProgressMemory(mutationId('coach-progress-memory'))
      await loadProgressMemory()
      setNotice(next.state === 'up_to_date' ? 'The current progress memory already includes all governed evidence.' : next.reused ? 'The current progress synthesis is already running or complete.' : 'Private progress synthesis started. It creates no client content and publishes nothing.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The progress memory could not be updated. No client content was changed.')
    } finally {
      setBusy('')
    }
  }

  async function restartOnboarding() {
    if (!onboarding || onboarding.state !== 'submitted' || restartPhrase !== ONBOARDING_RESTART_CONFIRMATION || !restartAcknowledged) return
    setBusy('restart-onboarding')
    setError('')
    setNotice('')
    try {
      const result = await repository.restartOnboardingTest({
        sessionId: onboarding.sessionId,
        expectedRevision: onboarding.revision,
        clientMutationId: restartMutationId,
        confirmation: ONBOARDING_RESTART_CONFIRMATION,
      })
      setOnboarding(result.session)
      setRestartAcknowledged(false)
      setRestartPhrase('')
      setRestartMutationId(mutationId('onboarding-restart'))
      setNotice(`Baseline archived safely (${result.archivedResponseCount + result.archivedSensitiveResponseCount} answers; reference ${result.archiveId.slice(0, 10)}). Opening a fresh questionnaire now.`)
      onOnboardingRestarted?.()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The onboarding baseline could not be archived. Nothing was changed.')
      await repository.getOnboardingSession().then(setOnboarding).catch(() => undefined)
    } finally {
      setBusy('')
    }
  }
  function updateDraft(candidateId: string, patch: Partial<CoachCandidateEditableDraft>) {
    setDrafts((current) => ({ ...current, [candidateId]: { ...current[candidateId], ...patch } }))
  }

  async function saveCandidate(candidate: CoachCandidateRecord) {
    setBusy(candidate.candidateId)
    setError('')
    try {
      await repository.saveCoachCandidateRevision({
        candidateId: candidate.candidateId, expectedRevision: candidate.revision,
        editableDraft: drafts[candidate.candidateId], clientMutationId: mutationId('candidate-save'),
      })
      await loadCandidateSet(candidate.candidateSetId)
      setNotice('Draft revision saved with immutable lineage. The private generation reason was not changed.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Candidate revision could not be saved.')
    } finally {
      setBusy('')
    }
  }

  async function reviewCandidate(candidate: CoachCandidateRecord, decision: 'approved' | 'rejected') {
    setBusy(candidate.candidateId)
    setError('')
    try {
      const draftChanged = JSON.stringify(drafts[candidate.candidateId]) !== JSON.stringify(candidate.editableDraft)
      if (draftChanged) {
        await repository.saveCoachCandidateRevision({
          candidateId: candidate.candidateId, expectedRevision: candidate.revision,
          editableDraft: drafts[candidate.candidateId], clientMutationId: mutationId('candidate-save'),
        })
        const refreshed = await repository.getCoachCandidateSet(candidate.candidateSetId)
        const revised = refreshed.candidates.find((item) => item.candidateId === candidate.candidateId)
        if (!revised) throw new Error('Candidate revision could not be reloaded.')
        await repository.reviewCoachCandidate({
          candidateId: revised.candidateId, expectedRevision: revised.revision, decision,
          clientMutationId: mutationId(`candidate-${decision}`),
        })
      } else {
        await repository.reviewCoachCandidate({
          candidateId: candidate.candidateId, expectedRevision: candidate.revision, decision,
          clientMutationId: mutationId(`candidate-${decision}`),
        })
      }
      await loadCandidateSet(candidate.candidateSetId)
      setNotice(decision === 'approved' ? 'Candidate approved for explicit publication.' : 'Candidate rejected and excluded from publication.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Candidate review could not be saved.')
    } finally {
      setBusy('')
    }
  }

  async function publishApproved() {
    if (!bundle) return
    setBusy('publish')
    setError('')
    try {
      const result = await repository.publishCoachCandidateSet({
        candidateSetId: bundle.candidateSet.candidateSetId,
        expectedRevision: bundle.candidateSet.revision,
        clientMutationId: mutationId('candidate-publish'),
      })
      await Promise.all([loadCandidateSet(bundle.candidateSet.candidateSetId), run ? loadRun(run.runId) : Promise.resolve()])
      setNotice(`${result.publishedCount} approved, validated practice${result.publishedCount === 1 ? '' : 's'} published to the client library. Private rationale and source facts were stripped.`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Approved candidates could not be published.')
    } finally {
      setBusy('')
    }
  }

  const initialGenerationPending = readiness?.initialGenerationPending === true
  const firstGenerationStatus = !readiness
    ? 'Checking your submitted baseline and consent state.'
    : initialGenerationPending && !readiness.consentCurrent
      ? 'Your onboarding is submitted. The first personalised generation is waiting for you to enable PFD AI personalisation in Privacy & data.'
      : initialGenerationPending && readiness.initialRequestState === 'queued_for_coach_generation'
        ? 'Your eligible onboarding baseline is queued and ready for the first Coach generation.'
        : readiness.initialRequestState === 'published'
          ? 'Your first Coach-approved library has been published. Future runs use an eligible Weekly Council and your explicit feedback.'
          : 'No personal coaching library is published yet. The User workspace will wait for Coach rather than showing generic material.'

  const readinessSummary = [
    { label: 'Days since publication', value: readiness?.daysSinceLastPublication === null ? 'New' : String(readiness?.daysSinceLastPublication ?? '—') },
    { label: 'Useful practices left', value: String(readiness?.usefulLibraryRunway ?? '—') },
    { label: 'Meaningful interactions', value: String(readiness?.meaningfulInteractions ?? '—') },
    { label: 'Helpful / poor fit', value: `${readiness?.helpfulAssets ?? '—'} / ${readiness?.poorFitAssets ?? '—'}` },
    { label: 'Safety route', value: readiness?.safetyTier ?? '—' },
  ]

  return (
    <main className="workspace coach-workspace">
      <section className="coach-hero">
        <div>
          <p className="eyebrow">Private coach workspace · owner trial</p>
          <h1>Review the week.<br /><em>Curate the next one.</em></h1>
          <p>Only this coach-authorised surface can start CustomGhost, edit validated drafts and publish a clean client projection.</p>
        </div>
        <div className="coach-client-card">
          <span className="micro-label">Active client</span>
          <div className="client-identity"><span>KC</span><div><strong>Koen</strong><small>Owner trial profile</small></div></div>
          <div className={`readiness-badge ${readiness?.ready ? '' : 'is-paused'}`}><span />{readiness?.ready ? 'Ready for coach generation' : 'Generation gate paused'}</div>
        </div>
      </section>

      <PostOnboardingGuide surface="coach" status={<div className="guide-status"><strong>Current account:</strong> {firstGenerationStatus}</div>} />

      <section className="coach-grid">
        <article className="weekly-readiness">
          <div className="panel-heading"><div><p className="eyebrow">Weekly intelligence</p><h2>Readiness packet</h2></div><span className="private-badge">Coach only</span></div>
          <div className="readiness-grid">{readinessSummary.map((item) => <div key={item.label}><strong>{item.value}</strong><span>{item.label}</span></div>)}</div>
          <div className={`weekly-queue-card queue-${readiness?.weeklyQueue?.state ?? 'missing'}`}>
            <div><span className="micro-label">Latest Weekly Council</span><strong>{readiness?.weeklyQueue ? `Week of ${readiness.weeklyQueue.weekId}` : 'No submitted council yet'}</strong></div>
            <span>{readiness?.weeklyQueue?.state.replaceAll('_', ' ') ?? 'required after initial generation'}</span>
            {readiness?.weeklyQueue?.reasonCodes.length ? <ul>{readiness.weeklyQueue.reasonCodes.map((code) => <li key={code}>{code.replaceAll('_', ' ')}</li>)}</ul> : null}
            <small>A user submission creates this coach-only queue. It never dispatches AI. Only Coach can consume one eligible queue.</small>
          </div>
          <div className="delta-summary">
            <h3>Generation boundaries</h3>
            <ul>
              <li>Only current AI-consented facts and compact feedback aggregates are sent.</li>
              <li>Raw journals, restricted safety answers and partner-direct claims are excluded.</li>
              <li>The current Coach-published personal library stays live through every generation failure. If none has been published, the personal workspace remains empty and waits for Coach.</li>
              <li>Every returned candidate waits here for explicit coach review.</li>
              <li>Adaptive candidates declare whether they are new or revise a named active practice; publication records retain, revise and retire lineage in one active library version.</li>
            </ul>
          </div>
          <button type="button" className="coach-run-button" disabled={!readiness?.ready || busy === 'run' || Boolean(run && activeRunStates.has(run.state))} onClick={() => void startRun('standard')}>
            {busy === 'run' ? 'Starting secure run…' : run && activeRunStates.has(run.state) ? 'CustomGhost run in progress…' : initialGenerationPending ? 'Generate first personalised coaching library' : 'Run PMM relationship intelligence'}
          </button>
          <div className="full-library-refresh">
            <div><strong>Review the new guided practice model</strong><span>Create 18 entirely fresh personal practices using the submitted baseline, current consented facts and the new action-first, story-led contract. Nothing is published automatically.</span></div>
            <button type="button" disabled={!readiness?.fullLibraryRefreshAvailable || busy === 'full-refresh' || Boolean(run && activeRunStates.has(run.state))} onClick={() => void startRun('full_library_refresh')}>
              {busy === 'full-refresh' ? 'Starting fresh 18.' : 'Generate fresh 18 for Coach review'}
            </button>
          </div>
          <div className="full-library-refresh progress-memory-control">
            <div>
              <strong>Coach progress memory</strong>
              <span>Manually update the private rolling understanding used for future Coach-reviewed Personalisation. This creates no practices, publishes nothing and never runs automatically.</span>
              <small>{progressMemory?.latest ? 'Latest snapshot ' + new Date(progressMemory.latest.createdAt).toLocaleDateString() + ' · ' + progressMemory.latest.sourceCount + ' governed sources' : 'No progress snapshot yet.'}{progressMemory ? ' · ' + progressMemory.readiness.newEvidenceCount + ' new evidence source' + (progressMemory.readiness.newEvidenceCount === 1 ? '' : 's') + ' available' : ''}</small>
            </div>
            <button type="button" disabled={busy === 'progress-memory' || !progressMemory?.readiness.canRun || Boolean(progressMemory?.latestRun && activeProgressStates.has(progressMemory.latestRun.state))} onClick={() => void updateProgressMemory()}>
              {busy === 'progress-memory' ? 'Starting private synthesis…' : progressMemory?.latestRun && activeProgressStates.has(progressMemory.latestRun.state) ? 'Progress synthesis in progress…' : progressMemory?.readiness.canRun ? 'Update progress memory' : 'Progress memory is current'}
            </button>
          </div>
          {!readiness?.consentCurrent && <p className="prototype-warning">AI personalisation consent is required in Privacy & data.</p>}
          {readiness?.reviewRecommendation === 'continue_current_library' && !initialGenerationPending && <p className="prototype-warning">The current personal library still has useful runway, or the man explicitly asked for more time. Keep coaching with what is live; no fresh generation is due.</p>}
          {readiness?.reviewRecommendation === 'review_weekly_council' && !initialGenerationPending && <p className="prototype-warning">Submit an eligible Weekly Council in User workspace before the next adaptive run.</p>}
          {readiness?.safetyTier === 'urgent' && <p className="prototype-warning">Urgent safety routing pauses relationship generation.</p>}
          {run && <div className={`run-status run-${run.state}`}><span>{run.state.replaceAll('_', ' ')}</span><p>{run.statusMessage}</p><small>{run.attemptCount} controlled attempt{run.attemptCount === 1 ? '' : 's'} · canonical run {run.runId.slice(-10)}</small></div>}
        </article>

        <aside className="coach-principles">
          <p className="eyebrow">Publication contract</p><h2>AI proposes.<br />The coach decides.</h2>
          <ol><li><span>01</span>Read the locked private reason.</li><li><span>02</span>Edit only client-delivery fields.</li><li><span>03</span>Approve or reject each candidate.</li><li><span>04</span>Publish approved projections atomically.</li></ol>
          <p>Reasons, source facts, validation evidence and raw provider output never enter User workspace.</p>
        </aside>
      </section>


      <section className="owner-trial-reset" aria-labelledby="owner-trial-reset-title">
        <div className="owner-trial-reset-copy">
          <div className="panel-heading">
            <div><p className="eyebrow">Owner trial controls</p><h2 id="owner-trial-reset-title">Restart onboarding test</h2></div>
            <span className="private-badge">Owner coach only</span>
          </div>
          <p>Run the complete first-day questionnaire again without overwriting your relationship history. The server seals the current working answers in a private archive before it reopens Chapter 1.</p>
          <ul>
            <li>Preserved: immutable onboarding snapshots, relationship facts and spine versions.</li>
            <li>Preserved: current generated assets, daily responses, journals and interactions.</li>
            <li>Reopened: only the editable onboarding response workspace.</li>
          </ul>
          <small>After restart, new coach generation stays paused until the fresh questionnaire is submitted. Your last approved library remains available throughout.</small>
        </div>
        <div className="owner-trial-reset-action">
          <span className={`onboarding-state state-${onboarding?.state ?? 'loading'}`}>
            {onboarding?.state === 'submitted' ? `Baseline ready ? revision ${onboarding.revision}` : onboarding?.state === 'in_progress' ? 'Questionnaire already open' : 'Checking baseline'}
          </span>
          <label className="restart-check">
            <input type="checkbox" checked={restartAcknowledged} onChange={(event) => setRestartAcknowledged(event.target.checked)} disabled={onboarding?.state !== 'submitted' || busy === 'restart-onboarding'} />
            <span>I understand the submitted baseline is archived, not erased.</span>
          </label>
          <label className="restart-phrase">
            <span>Type <strong>{ONBOARDING_RESTART_CONFIRMATION}</strong></span>
            <input value={restartPhrase} onChange={(event) => setRestartPhrase(event.target.value)} autoComplete="off" disabled={onboarding?.state !== 'submitted' || busy === 'restart-onboarding'} />
          </label>
          <button type="button" className="restart-onboarding-button" disabled={onboarding?.state !== 'submitted' || !restartAcknowledged || restartPhrase !== ONBOARDING_RESTART_CONFIRMATION || Boolean(busy)} onClick={() => void restartOnboarding()}>
            {busy === 'restart-onboarding' ? 'Archiving safely?' : 'Archive baseline and restart'}
          </button>
          {onboarding?.state !== 'submitted' && <p className="prototype-warning">Complete and submit the current questionnaire before a completed baseline can be archived.</p>}
        </div>
      </section>
      <section className={`candidate-section ${bundle ? 'is-ready' : ''}`} aria-labelledby="candidate-title">
        <div className="section-heading"><div><p className="eyebrow">Candidate review</p><h2 id="candidate-title">Edit before anything reaches the client</h2></div><p>{bundle ? `${bundle.candidates.length} private candidates · ${counts.approved} approved · ${counts.awaiting} awaiting` : 'A validated CustomGhost result unlocks this review gate.'}</p></div>
        {!bundle && <div className="locked-review"><span>Private review gate</span><h3>No unreviewed AI content can enter the client library.</h3><p>The current coach-published personal library stays active while a new run is waiting, invalid or unavailable.</p></div>}
        {bundle && <div className="candidate-list">{bundle.candidates.map((candidate, index) => {
          const draft = drafts[candidate.candidateId] ?? editableDraftFor(candidate)
          const locked = candidate.reviewState === 'published'
          return <article className="candidate-card" key={candidate.candidateId}>
            <header><div><span className="candidate-number">Candidate {String(index + 1).padStart(2, '0')}</span><strong>{candidate.generatedAsset.lane === 'self' ? 'Self development' : candidate.generatedAsset.lane === 'relationship' ? 'Relationship development' : 'Fatherhood & family'}</strong></div><span className={`review-state state-${candidate.reviewState}`}>{candidate.reviewState.replaceAll('_', ' ')}</span></header>
            <div className="candidate-layout">
              <aside className="coach-reason"><div className="locked-label"><span>Locked</span> Why PFD generated this</div><p>{candidate.coachReason}</p><div className="reason-codes"><span>{candidate.lifecycleOperation}{candidate.supersedesAssetId ? ` · replaces ${candidate.supersedesAssetId}` : ''}</span>{candidate.coachReasonCodes.map((code) => <span key={code}>{code.replaceAll('_', ' ')}</span>)}</div><small>Immutable · coach/server only · {candidate.sourceFactIds.length} source fact ID{candidate.sourceFactIds.length === 1 ? '' : 's'} · never included in client projection</small></aside>
              <div className="candidate-editor">
                <label><span>Client title</span><input disabled={locked} value={draft.title} onChange={(event) => updateDraft(candidate.candidateId, { title: event.target.value })} /></label>
                <label><span>Purpose</span><textarea disabled={locked} rows={2} value={draft.purpose} onChange={(event) => updateDraft(candidate.candidateId, { purpose: event.target.value })} /></label>
                <div className="candidate-guidance-meta">
                  <span>{candidate.generatedAsset.assetFormat?.replaceAll('_', ' ') ?? 'legacy format'}</span>
                  <span>Thinking: {candidate.generatedAsset.cognitiveDemand ?? candidate.generatedAsset.effort}</span>
                  <span>Emotional load: {candidate.generatedAsset.emotionalDemand ?? candidate.generatedAsset.effort}</span>
                </div>
                <label><span>Plain-language explanation</span><textarea disabled={locked} rows={3} value={draft.plainLanguageExplanation} onChange={(event) => updateDraft(candidate.candidateId, { plainLanguageExplanation: event.target.value })} /></label>
                <label><span>Composite recognition story - let the human moment breathe</span><textarea disabled={locked} rows={10} value={draft.recognitionStory} onChange={(event) => updateDraft(candidate.candidateId, { recognitionStory: event.target.value })} /></label>
                <label><span>Real-life examples - one per line</span><textarea disabled={locked} rows={4} value={draft.realLifeExamples.join('\n')} onChange={(event) => updateDraft(candidate.candidateId, { realLifeExamples: event.target.value.split('\n').map((item) => item.trim()).filter(Boolean) })} /></label>
                <label><span>Instructions · one per line</span><textarea disabled={locked} rows={5} value={draft.instructions.join('\n')} onChange={(event) => updateDraft(candidate.candidateId, { instructions: event.target.value.split('\n').map((item) => item.trim()).filter(Boolean) })} /></label>
                <label><span>Response prompt</span><input disabled={locked} value={draft.responsePrompt} onChange={(event) => updateDraft(candidate.candidateId, { responsePrompt: event.target.value })} /></label>
                <label><span>Response field</span><select disabled={locked} value={draft.responseKind} onChange={(event) => updateDraft(candidate.candidateId, { responseKind: event.target.value as CoachCandidateEditableDraft['responseKind'] })}><option value="short_text">Short text</option><option value="long_text">Long reflection</option><option value="scale">0–10 scale</option><option value="checklist">Checklist</option><option value="plan">Action plan</option></select></label>
                <label><span>Recognition choices - one per line</span><textarea disabled={locked} rows={5} value={draft.responseOptions.join('\n')} onChange={(event) => updateDraft(candidate.candidateId, { responseOptions: event.target.value.split('\n').map((item) => item.trim()).filter(Boolean) })} /></label>
                <label><span>Three honest example answers - one per line</span><textarea disabled={locked} rows={6} value={draft.exampleAnswers.join('\n')} onChange={(event) => updateDraft(candidate.candidateId, { exampleAnswers: event.target.value.split('\n').map((item) => item.trim()).filter(Boolean) })} /></label>
                <label><span>Lighter alternative</span><textarea disabled={locked} rows={2} value={draft.lighterAlternative} onChange={(event) => updateDraft(candidate.candidateId, { lighterAlternative: event.target.value })} /></label>
                <div className="candidate-toggles">
                  <label><input type="checkbox" disabled={locked} checked={draft.responseOptional} onChange={(event) => updateDraft(candidate.candidateId, { responseOptional: event.target.checked })} /><span>Response is optional</span></label>
                  <label><input type="checkbox" disabled={locked} checked={draft.freeTextOptional} onChange={(event) => updateDraft(candidate.candidateId, { freeTextOptional: event.target.checked })} /><span>Private free text is optional</span></label>
                </div>
                <label><span>Completion signal</span><input disabled={locked} value={draft.completionSignal} onChange={(event) => updateDraft(candidate.candidateId, { completionSignal: event.target.value })} /></label>
              </div>
            </div>
            {!locked && <footer><button type="button" className="reject-action" disabled={busy === candidate.candidateId} onClick={() => void reviewCandidate(candidate, 'rejected')}>Reject</button><button type="button" className="quiet-action" disabled={busy === candidate.candidateId} onClick={() => void saveCandidate(candidate)}>Save draft</button><button type="button" className="approve-action" disabled={busy === candidate.candidateId} onClick={() => void reviewCandidate(candidate, 'approved')}>Approve candidate</button></footer>}
          </article>
        })}</div>}
        {bundle && bundle.candidateSet.state !== 'published' && <div className="publish-bar"><div><strong>{counts.approved} approved · {counts.rejected} rejected</strong><span>Only approved, freshly revalidated projections can enter the client library.</span></div><button type="button" disabled={counts.approved === 0 || busy === 'publish'} onClick={() => void publishApproved()}>{busy === 'publish' ? 'Publishing atomically…' : 'Publish approved assets'}</button></div>}
      </section>

      {(notice || error) && <div className={`toast coach-toast ${error ? 'is-error' : ''}`} role={error ? 'alert' : 'status'}><span />{error || notice}<button type="button" onClick={() => { setNotice(''); setError('') }} aria-label="Dismiss message">×</button></div>}
    </main>
  )
}
